/* eslint-disable max-lines */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Grid,
  Card,
  CardContent,
  CardActions,
  Collapse,
  Slider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Star as DefaultIcon,
  Code as CodeIcon,
  Lock as ImmutableIcon,
  LockPerson as LockedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Preview as PreviewIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

// eslint-disable-next-line max-lines-per-function
const PromptManagement = () => {
  useAuth();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [showVariableHelp, setShowVariableHelp] = useState(false);
  const [openVersionHistory, setOpenVersionHistory] = useState(false);
  const [selectedPromptForHistory, setSelectedPromptForHistory] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Advanced search and filter state
  const [filterByCreator, _setFilterByCreator] = useState('all');
  const [filterByStatus, _setFilterByStatus] = useState('all');
  const [filterByLocked, _setFilterByLocked] = useState('all');
  const [sortBy, _setSortBy] = useState('updatedAt');
  const [sortDirection, _setSortDirection] = useState('desc');

  // Variable management state
  const [availableVariables, setAvailableVariables] = useState([]);
  const [showVariablePanel, setShowVariablePanel] = useState(false);
  const [templateTextRef, setTemplateTextRef] = useState(null);
  const [variableSearchTerm, setVariableSearchTerm] = useState('');
  const [showModelSettings, setShowModelSettings] = useState(false);

  // SNA Preview state
  const [snaPreviewOpen, setSnaPreviewOpen] = useState(false);
  const [snaPreviewData, setSnaPreviewData] = useState(null);
  const [snaPreviewLoading, setSnaPreviewLoading] = useState(false);
  const [snaPreviewError, setSnaPreviewError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: '',
    category: 'chat',
    isDefault: false,
    locked: false,
    versionTag: 'draft',
    metadata: {
      model: 'gemini-2.0-flash',
      temperature: 0.7,
      maxTokens: 2048,
    },
  });

  // Categories - loaded dynamically from API
  const [categories, setCategories] = useState([]);

  // Version Tags
  const VERSION_TAGS = {
    production: { label: 'Production', color: '#4caf50', bgcolor: '#e8f5e9' },
    testing: { label: 'Testing', color: '#ff9800', bgcolor: '#fff3e0' },
    draft: { label: 'Draft', color: '#2196f3', bgcolor: '#e3f2fd' },
    experimental: { label: 'Experimental', color: '#9c27b0', bgcolor: '#f3e5f5' }
  };

  useEffect(() => {
    loadCategories();
    loadAvailableVariables();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadPrompts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, showInactive, filterByCreator, filterByStatus, filterByLocked, sortBy, sortDirection]);

  const formatCategoryLabel = (category) => {
    if (!category) return '';
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const loadCategories = async () => {
    try {
      const response = await api.getPromptCategories();

      if (response.success && response.categories) {
        const formattedCategories = response.categories.map(cat => ({
          value: cat,
          label: formatCategoryLabel(cat)
        }));

        formattedCategories.sort((a, b) => a.label.localeCompare(b.label));

        setCategories([
          { value: 'all', label: 'All Categories' },
          ...formattedCategories
        ]);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Leave categories empty on error
    }
  };

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const filters = {
        isActive: !showInactive,
        includeVersions: false,
      };

      if (selectedCategory !== 'all') {
        filters.category = selectedCategory;
      }

      const response = await api.listPrompts(filters);
      setPrompts(response.prompts || []);
    } catch (error) {
      console.error('Failed to load prompts:', error);
      setError('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableVariables = async () => {
    try {
      // Conversation history, the current message, and tool working memory are
      // handled structurally by the chat engine and are no longer prompt vars.
      const systemVariables = [
        {
          name: 'entityName',
          description: 'Name of the current entity/organization',
          type: 'string',
          required: false,
          category: 'Entity'
        },
        {
          name: 'entityId',
          description: 'Unique identifier for the current entity',
          type: 'string',
          required: false,
          category: 'Entity'
        },
        {
          name: 'userName',
          description: 'Name of the current user',
          type: 'string',
          required: false,
          category: 'User'
        },
        {
          name: 'userEmail',
          description: 'Email address of the current user',
          type: 'string',
          required: false,
          category: 'User'
        },
        {
          name: 'roleType',
          description: 'Role or position type (e.g., leader, coach, manager)',
          type: 'string',
          required: false,
          category: 'Context'
        },
        {
          name: 'industryType',
          description: 'Industry or business sector',
          type: 'string',
          required: false,
          category: 'Context'
        },
        {
          name: 'teamSize',
          description: 'Size of the team or organization',
          type: 'number',
          required: false,
          category: 'Context'
        },
        // SNA (Social Network Analysis) variables
        {
          name: 'socialNetworkImpact',
          description: 'Injects social network analysis context from indexed documents for the current entity. Returns recent activity highlights, relationship summaries, and document coverage statistics.',
          type: 'string',
          required: false,
          category: 'SNA',
          hasPreview: true,
          previewDescription: 'Click to preview SNA data for the selected entity'
        }
      ];

      setAvailableVariables(systemVariables);
    } catch (error) {
      console.error('Failed to load variables:', error);
    }
  };

  // Handler for SNA preview
  const handleSnaPreview = async () => {
    const selectedEntityId = localStorage.getItem('selectedEntityId');
    if (!selectedEntityId) {
      setSnaPreviewError('Please select an entity first to preview SNA data');
      setSnaPreviewOpen(true);
      return;
    }

    setSnaPreviewLoading(true);
    setSnaPreviewError(null);
    setSnaPreviewOpen(true);

    try {
      const response = await api.getSNAImpactPreview(selectedEntityId);
      setSnaPreviewData(response);
    } catch (error) {
      console.error('Failed to fetch SNA preview:', error);
      setSnaPreviewError(error.message || 'Failed to fetch SNA impact data');
    } finally {
      setSnaPreviewLoading(false);
    }
  };

  const handleCreatePrompt = () => {
    setEditingPrompt(null);
    setFormData({
      name: '',
      description: '',
      template: '',
      category: 'chat',
      isDefault: false,
      locked: false,
      metadata: {
        model: 'gemini-2.0-flash',
        temperature: 0.7,
        maxTokens: 2048,
      },
    });
    setOpenDialog(true);
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      template: prompt.template,
      category: prompt.category || 'general',
      isDefault: prompt.isDefault || false,
      locked: prompt.locked || false,
      versionTag: prompt.versionTag || 'draft',
      metadata: prompt.metadata || {
        model: 'gemini-2.0-flash',
        temperature: 0.7,
        maxTokens: 2048,
      },
    });
    setOpenDialog(true);
  };

  const handleSavePrompt = async () => {
    try {
      setLoading(true);

      if (editingPrompt) {
        // Update existing prompt (creates new version)
        await api.updatePrompt({
          promptId: editingPrompt.id,
          ...formData,
        });
        setSuccess('Prompt updated successfully (new version created)');
      } else {
        // Create new prompt
        await api.createPrompt(formData);
        setSuccess('Prompt created successfully');
      }

      setOpenDialog(false);
      loadPrompts();
    } catch (error) {
      console.error('Failed to save prompt:', error);
      setError('Failed to save prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicatePrompt = async (prompt) => {
    try {
      setLoading(true);
      await api.createPrompt({
        ...prompt,
        name: `${prompt.name} (Copy)`,
        isDefault: false,
      });
      setSuccess('Prompt duplicated successfully');
      loadPrompts();
    } catch (error) {
      console.error('Failed to duplicate prompt:', error);
      setError('Failed to duplicate prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (prompt) => {
    // Check if prompt is immutable (gracefully handle missing field)
    if (prompt.immutable === true) {
      setError('Cannot delete immutable system prompts');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the prompt "${prompt.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await api.deletePrompt(prompt.id);
      setSuccess('Prompt deleted successfully');
      loadPrompts();
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      setError('Failed to delete prompt');
    } finally {
      setLoading(false);
    }
  };

  const handleShowVersionHistory = async (prompt) => {
    setSelectedPromptForHistory(prompt);
    setOpenVersionHistory(true);
    setLoadingVersions(true);

    try {
      const response = await api.getPromptVersions(prompt.name);
      setVersions(response.versions || []);
    } catch (error) {
      console.error('Failed to load versions:', error);
      setError('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleRestoreVersion = async (versionId) => {
    try {
      setLoading(true);
      await api.restorePromptVersion(versionId);
      setSuccess('Version restored successfully');
      setOpenVersionHistory(false);
      loadPrompts();
    } catch (error) {
      console.error('Failed to restore version:', error);
      setError('Failed to restore version');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVersion = async (versionId) => {
    if (!window.confirm('Are you sure you want to delete this version? This cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await api.deletePromptVersion(versionId);
      setSuccess('Version deleted successfully');
      // Reload versions
      if (selectedPromptForHistory) {
        const response = await api.getPromptVersions(selectedPromptForHistory.name);
        setVersions(response.versions || []);
      }
    } catch (error) {
      console.error('Failed to delete version:', error);
      setError(error.message || 'Failed to delete version');
    } finally {
      setLoading(false);
    }
  };

  const extractVariables = (template) => {
    const regex = /\{([^}]+)\}/g;
    const variables = new Set();
    let match;

    while ((match = regex.exec(template)) !== null) {
      variables.add(match[1]);
    }

    return Array.from(variables);
  };

  // eslint-disable-next-line max-lines-per-function
  const renderPromptCard = (prompt) => {
    const variables = extractVariables(prompt.template);

    return (
      <Card key={prompt.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography variant="h6" component="div">
                {prompt.name}
                {prompt.isDefault && (
                  <Tooltip title="Default prompt for this category">
                    <DefaultIcon sx={{ ml: 1, color: 'warning.main', fontSize: 20 }} />
                  </Tooltip>
                )}
                {prompt.immutable === true && (
                  <Tooltip title="System prompt - cannot be deleted">
                    <ImmutableIcon sx={{ ml: 1, color: 'secondary.main', fontSize: 20 }} />
                  </Tooltip>
                )}
                {prompt.locked === true && (
                  <Tooltip title="Locked prompt - only creator or system admin can edit">
                    <LockedIcon sx={{ ml: 1, color: 'error.main', fontSize: 20 }} />
                  </Tooltip>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {prompt.description || 'No description'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={prompt.category || 'general'}
                size="small"
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`v${prompt.version}`}
                size="small"
                variant="outlined"
              />
              {prompt.versionTag && (
                <Chip
                  label={VERSION_TAGS[prompt.versionTag]?.label || prompt.versionTag}
                  size="small"
                  sx={{
                    bgcolor: VERSION_TAGS[prompt.versionTag]?.bgcolor || '#f5f5f5',
                    color: VERSION_TAGS[prompt.versionTag]?.color || '#666',
                    fontWeight: 'medium'
                  }}
                />
              )}
              <Chip
                icon={prompt.isActive ? <ActiveIcon /> : <InactiveIcon />}
                label={prompt.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={prompt.isActive ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Variables: {variables.length > 0 ? variables.join(', ') : 'None'}
            </Typography>
            {prompt.metadata && (
              <Typography variant="caption" color="text.secondary">
                Model: {prompt.metadata.model || 'default'} |
                Temp: {prompt.metadata.temperature || 'default'} |
                Max Tokens: {prompt.metadata.maxTokens || 'default'}
              </Typography>
            )}
          </Box>

          <Box sx={{
            p: 2,
            bgcolor: 'grey.100',
            borderRadius: 1,
            maxHeight: 200,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            whiteSpace: 'pre-wrap',
          }}>
            {prompt.template.substring(0, 500)}
            {prompt.template.length > 500 && '...'}
          </Box>
        </CardContent>

        <CardActions sx={{ justifyContent: 'flex-end' }}>
          <Tooltip title="Version history">
            <IconButton onClick={() => handleShowVersionHistory(prompt)} size="small">
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit prompt">
            <IconButton onClick={() => handleEditPrompt(prompt)} size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicate prompt">
            <IconButton onClick={() => handleDuplicatePrompt(prompt)} size="small">
              <CopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={prompt.immutable === true ? "Cannot delete system prompts" : "Delete prompt"}>
            <span>
              <IconButton
                onClick={() => handleDeletePrompt(prompt)}
                size="small"
                disabled={prompt.immutable === true}
                color={prompt.immutable === true ? "default" : "error"}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </CardActions>
      </Card>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Prompt Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreatePrompt}
        >
          Create Prompt
        </Button>
      </Box>
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="All Prompts" />
          <Tab label="By Category" />
        </Tabs>
      </Paper>
      {tabValue === 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Category"
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
              }
              label="Show inactive"
            />
          </Box>

          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Box>
              {prompts.map(prompt => renderPromptCard(prompt))}
              {prompts.length === 0 && (
                <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No prompts found
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {categories.filter(c => c.value !== 'all').map(category => (
            <Grid
              key={category.value}
              size={{
                xs: 12,
                md: 6
              }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {category.label}
                </Typography>
                {prompts
                  .filter(p => p.category === category.value)
                  .map(prompt => renderPromptCard(prompt))}
                {prompts.filter(p => p.category === category.value).length === 0 && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No prompts in this category
                  </Typography>
                )}
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingPrompt ? `Edit Prompt: ${editingPrompt.name}` : 'Create New Prompt'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                label="Category"
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {categories.filter(c => c.value !== 'all').map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Version Tag</InputLabel>
              <Select
                value={formData.versionTag || 'draft'}
                label="Version Tag"
                onChange={(e) => setFormData({ ...formData, versionTag: e.target.value })}
              >
                {Object.entries(VERSION_TAGS).map(([key, config]) => (
                  <MenuItem key={key} value={key}>
                    <Chip
                      size="small"
                      label={config.label}
                      sx={{
                        bgcolor: config.bgcolor,
                        color: config.color,
                        fontWeight: 'medium'
                      }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
              }
              label="Set as default for this category"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.locked}
                  onChange={(e) => setFormData({ ...formData, locked: e.target.checked })}
                />
              }
              label={
                <Tooltip title="When locked, only the creator can edit or delete this prompt. System admins can override locks.">
                  <span>🔒 Lock this prompt (restrict editing to creator)</span>
                </Tooltip>
              }
            />

            {/* Variable Documentation Section - Collapsible */}
            <Box>
              <Button
                onClick={() => setShowVariableHelp(!showVariableHelp)}
                startIcon={showVariableHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  mb: 1,
                  textTransform: 'none',
                  color: 'info.main',
                  '&:hover': { bgcolor: 'info.light' }
                }}
              >
                How Variables Work - Documentation & Examples
              </Button>

              <Collapse in={showVariableHelp}>
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.light', border: 1, borderColor: 'info.main' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: 'info.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CodeIcon />
                    Understanding AI Memory & Prompt Templates
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, color: 'info.dark', fontWeight: 'bold' }}>
                    ⚠️ Important: AI doesn't actually "remember" your conversations!
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, color: 'info.dark' }}>
                    Unlike humans, AI language models don't have memory between interactions. Each time you send a message,
                    the AI starts completely fresh - it doesn't know who you are, what you talked about before, or any context
                    about your situation. This is why prompt templates are critical.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, color: 'info.dark' }}>
                    <strong>How the "memory illusion" works:</strong> Every time you chat, the system secretly packages up your
                    entire conversation history and sends it along with your new message. The AI sees everything each time,
                    creating the illusion it "remembers" you.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, color: 'info.dark' }}>
                    <strong>What the engine handles for you:</strong> Conversation history, the user's current message,
                    and the AI's tool working memory are supplied automatically by the chat engine on every request.
                    You no longer add <code>{'{history}'}</code>, <code>{'{input}'}</code>, or <code>{'{agent_scratchpad}'}</code>
                    to your template - the engine delivers them structurally, and tool use works without them.
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2, color: 'info.dark' }}>
                    <strong>What you control:</strong> Variables let you inject live data into the persona - the entity,
                    the user, their role, and social network context. Use them to make each response specific and grounded.
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.dark', mb: 1 }}>
                      Available Variables:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, ml: 2 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'info.dark' }}>
                        <strong>{'{entityName}'}</strong> / <strong>{'{entityId}'}</strong> - Current entity
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'info.dark' }}>
                        <strong>{'{userName}'}</strong> / <strong>{'{userEmail}'}</strong> - Current user
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'info.dark' }}>
                        <strong>{'{roleType}'}</strong> / <strong>{'{industryType}'}</strong> / <strong>{'{teamSize}'}</strong> - Context
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'info.dark' }}>
                        <strong>{'{socialNetworkImpact}'}</strong> - SNA context from indexed documents
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'info.dark', mb: 1 }}>
                      Example Usage:
                    </Typography>
                    <Paper sx={{ p: 1.5, bgcolor: 'grey.100', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      <Typography component="pre" sx={{ margin: 0, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
{`# Your Role
You are Faana AI, supporting {userName} as they coach {entityName}.

# Context
{socialNetworkImpact}

# Your Task
Ground every response in the entity's real data and lead with the insight.`}
                      </Typography>
                    </Paper>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'info.dark', fontStyle: 'italic' }}>
                    💡 Tip: Write the persona in plain language, then add variables like {'{userName}'} or {'{entityName}'} where you want live data injected.
                  </Typography>
                </Paper>
              </Collapse>
            </Box>

            {/* Template Editor with Variable Panel */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flexGrow: 1 }}>
                <TextField
                  label="Template"
                  value={formData.template}
                  onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  sx={{ fontFamily: 'monospace' }}
                  helperText="Write your prompt template using variables like {variableName}"
                  inputRef={setTemplateTextRef}
                />
              </Box>

              {/* Variable Panel */}
              <Box sx={{ width: 300, flexShrink: 0 }}>
                <Tooltip title="Click to browse and insert variables into your template">
                  <Button
                    onClick={() => setShowVariablePanel(!showVariablePanel)}
                    startIcon={showVariablePanel ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {showVariablePanel ? 'Hide' : 'Show'} Variables
                  </Button>
                </Tooltip>

                <Collapse in={showVariablePanel}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Available Variables
                    </Typography>

                    <TextField
                      size="small"
                      placeholder="Search variables..."
                      value={variableSearchTerm}
                      onChange={(e) => setVariableSearchTerm(e.target.value)}
                      fullWidth
                      sx={{ mb: 2 }}
                    />

                    {Object.entries(availableVariables
                      .filter(variable =>
                        variableSearchTerm === '' ||
                        variable.name.toLowerCase().includes(variableSearchTerm.toLowerCase()) ||
                        variable.description.toLowerCase().includes(variableSearchTerm.toLowerCase()) ||
                        variable.category.toLowerCase().includes(variableSearchTerm.toLowerCase())
                      )
                      .reduce((acc, variable) => {
                        if (!acc[variable.category]) {
                          acc[variable.category] = [];
                        }
                        acc[variable.category].push(variable);
                        return acc;
                      }, {})).map(([category, variables]) => (
                      <Box key={category} sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'primary.main', display: 'block', mb: 1 }}>
                          {category}
                        </Typography>
                        {variables.map((variable) => (
                          <Box
                            key={variable.name}
                            sx={{
                              mb: 1,
                              p: 1,
                              bgcolor: variable.category === 'SNA' ? '#e8f5e9' : 'white',
                              borderRadius: 1,
                              cursor: 'pointer',
                              border: variable.category === 'SNA' ? '1px solid #4caf50' : 'none',
                              '&:hover': { bgcolor: variable.category === 'SNA' ? '#c8e6c9' : 'primary.light' }
                            }}
                            onClick={() => {
                              const currentValue = formData.template;
                              const variableText = `{${variable.name}}`;

                              // Insert variable at cursor position or at the end
                              if (templateTextRef && templateTextRef.selectionStart !== undefined) {
                                const start = templateTextRef.selectionStart;
                                const end = templateTextRef.selectionEnd;
                                const newValue = currentValue.substring(0, start) + variableText + currentValue.substring(end);
                                setFormData({ ...formData, template: newValue });

                                // Move cursor after the inserted variable
                                setTimeout(() => {
                                  if (templateTextRef) {
                                    templateTextRef.setSelectionRange(start + variableText.length, start + variableText.length);
                                    templateTextRef.focus();
                                  }
                                }, 0);
                              } else {
                                // Fallback: append to end
                                setFormData({ ...formData, template: currentValue + variableText });
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: variable.category === 'SNA' ? '#2e7d32' : 'primary.dark' }}>
                                {'{' + variable.name + '}'}
                                {variable.required && (
                                  <Chip label="Required" size="small" color="error" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} />
                                )}
                                {variable.category === 'SNA' && (
                                  <Chip
                                    label="SNA"
                                    size="small"
                                    sx={{ ml: 1, height: 16, fontSize: '0.65rem', bgcolor: '#4caf50', color: 'white' }}
                                  />
                                )}
                              </Typography>
                              {variable.hasPreview && (
                                <Tooltip title={variable.previewDescription || 'Preview this variable'}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (variable.name === 'socialNetworkImpact') {
                                        handleSnaPreview();
                                      }
                                    }}
                                    sx={{ ml: 1 }}
                                  >
                                    <PreviewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                              {variable.description}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ))}

                    <Typography variant="caption" sx={{
                      display: 'block',
                      mt: 2,
                      pt: 1,
                      borderTop: 1,
                      borderColor: 'divider',
                      color: 'text.secondary',
                      fontStyle: 'italic'
                    }}>
                      💡 Click any variable above to insert it at your cursor position
                    </Typography>
                  </Paper>
                </Collapse>
              </Box>
            </Box>

            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Detected Variables ({extractVariables(formData.template).length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {extractVariables(formData.template).length > 0 ? (
                  extractVariables(formData.template).map((varName) => {
                    const variable = availableVariables.find(v => v.name === varName);
                    return (
                      <Chip
                        key={varName}
                        label={`{${varName}}`}
                        size="small"
                        variant={variable ? "filled" : "outlined"}
                        color={variable?.required ? "error" : variable ? "primary" : "default"}
                        title={variable ? variable.description : "Custom variable (not in standard set)"}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      />
                    );
                  })
                ) : (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No variables detected yet. Start typing {'{variableName}'} in your template.
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Model Settings Section - Collapsible */}
            <Box sx={{ mt: 2 }}>
              <Button
                onClick={() => setShowModelSettings(!showModelSettings)}
                startIcon={showModelSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{
                  mb: 1,
                  textTransform: 'none',
                  color: 'secondary.main',
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: 'secondary.light' }
                }}
              >
                Model Settings
                {!showModelSettings && (
                  <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary', fontWeight: 'normal' }}>
                    ({formData.metadata.model} • Temp: {formData.metadata.temperature} • Tokens: {formData.metadata.maxTokens})
                  </Typography>
                )}
              </Button>

              <Collapse in={showModelSettings}>
                <Box>
                  <TextField
                    label="Model"
                    value={formData.metadata.model}
                    onChange={(e) => setFormData({
                      ...formData,
                      metadata: { ...formData.metadata, model: e.target.value }
                    })}
                    fullWidth
                    helperText="The AI model to use (e.g., gemini-2.0-flash, gpt-4, etc.)"
                    sx={{ mb: 2 }}
                  />

                  {/* Model Settings Explanation */}
                  <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'grey.300' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.primary', mb: 1 }}>
                      🎛️ Understanding Model Settings
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        Temperature controls creativity vs consistency:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                        Lower values (0.0-0.3) = More predictable and focused responses
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                        Higher values (0.8-2.0) = More creative and varied responses
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        Max Tokens controls response length:
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1 }}>
                        Range: 256-8192 tokens (roughly 200-6000 words)
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', ml: 1, fontStyle: 'italic' }}>
                        💡 Higher limits cost more and take longer to generate
                      </Typography>
                    </Box>
                  </Paper>

                  <Grid container spacing={4}>
                    <Grid size={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Temperature: {formData.metadata.temperature}
                      </Typography>
                      <Slider
                        value={formData.metadata.temperature}
                        onChange={(_, value) => setFormData({
                          ...formData,
                          metadata: { ...formData.metadata, temperature: value }
                        })}
                        min={0}
                        max={2}
                        step={0.1}
                        marks={[
                          { value: 0, label: '0' },
                          { value: 0.3, label: '0.3' },
                          { value: 0.7, label: '0.7' },
                          { value: 1.0, label: '1.0' },
                          { value: 2.0, label: '2.0' }
                        ]}
                        valueLabelDisplay="auto"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formData.metadata.temperature < 0.4 ? '🎯 Very focused and consistent' :
                         formData.metadata.temperature < 0.8 ? '⚖️ Balanced creativity and consistency' :
                         formData.metadata.temperature < 1.2 ? '🎨 Creative and varied responses' :
                         '🌪️ Very creative, sometimes unpredictable'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Max Tokens: {formData.metadata.maxTokens}
                      </Typography>
                      <Slider
                        value={formData.metadata.maxTokens}
                        onChange={(_, value) => setFormData({
                          ...formData,
                          metadata: { ...formData.metadata, maxTokens: value }
                        })}
                        min={256}
                        max={8192}
                        step={256}
                        marks={[
                          { value: 256, label: '256' },
                          { value: 1024, label: '1K' },
                          { value: 2048, label: '2K' },
                          { value: 4096, label: '4K' },
                          { value: 8192, label: '8K' }
                        ]}
                        valueLabelDisplay="auto"
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {formData.metadata.maxTokens < 512 ? '💬 Brief responses (text message)' :
                         formData.metadata.maxTokens < 1500 ? '📧 Medium responses (email length)' :
                         formData.metadata.maxTokens < 4000 ? '📄 Long responses (article length)' :
                         '📚 Very long responses (detailed report)'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSavePrompt}
            variant="contained"
            disabled={!formData.name || !formData.template}
          >
            {editingPrompt ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Version History Dialog */}
      <Dialog
        open={openVersionHistory}
        onClose={() => setOpenVersionHistory(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Version History: {selectedPromptForHistory?.name}
        </DialogTitle>
        <DialogContent>
          {loadingVersions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography>Loading versions...</Typography>
            </Box>
          ) : (
            <List>
              {versions.map((version) => (
                <ListItem key={version.id} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6">v{version.version}</Typography>
                        {version.versionTag && (
                          <Chip
                            size="small"
                            label={VERSION_TAGS[version.versionTag]?.label || version.versionTag}
                            sx={{
                              bgcolor: VERSION_TAGS[version.versionTag]?.bgcolor || '#f5f5f5',
                              color: VERSION_TAGS[version.versionTag]?.color || '#666',
                              fontWeight: 'medium'
                            }}
                          />
                        )}
                        {version.isActive && (
                          <Chip size="small" label="Active" color="success" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Created by {version.createdBy} on {new Date(version.createdAt._seconds * 1000).toLocaleString()}
                        </Typography>
                        {version.restoredAt && (
                          <Typography variant="body2" color="text.secondary">
                            Restored by {version.restoredBy} on {new Date(version.restoredAt._seconds * 1000).toLocaleString()}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {version.description || 'No description'}
                        </Typography>
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!version.isActive && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleRestoreVersion(version.id)}
                        >
                          Restore
                        </Button>
                      )}
                      {versions.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteVersion(version.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVersionHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* SNA Preview Dialog */}
      <Dialog
        open={snaPreviewOpen}
        onClose={() => setSnaPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HubIcon color="primary" />
          SNA Impact Preview
        </DialogTitle>
        <DialogContent>
          {snaPreviewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <Typography>Loading SNA data...</Typography>
            </Box>
          ) : snaPreviewError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {snaPreviewError}
            </Alert>
          ) : snaPreviewData ? (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This is a preview of what the <code>{'{socialNetworkImpact}'}</code> variable will inject into your prompt for entity: <strong>{snaPreviewData.entityId}</strong>
              </Alert>

              {/* Stats Summary */}
              {snaPreviewData.stats && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Document Coverage
                  </Typography>
                  <Typography variant="body2">
                    Total indexed documents: {snaPreviewData.stats.totalDocuments || 0}
                  </Typography>
                  {snaPreviewData.stats.documentsBySourceType && (
                    <Box sx={{ mt: 1 }}>
                      {Object.entries(snaPreviewData.stats.documentsBySourceType).map(([type, count]) => (
                        <Chip
                          key={type}
                          label={`${type}: ${count}`}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                  {snaPreviewData.stats.lastIndexedAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Last indexed: {new Date(snaPreviewData.stats.lastIndexedAt).toLocaleString()}
                    </Typography>
                  )}
                </Paper>
              )}

              {/* Recent Documents */}
              {snaPreviewData.recentDocuments && snaPreviewData.recentDocuments.length > 0 && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Recent Activity Highlights
                  </Typography>
                  <List dense>
                    {snaPreviewData.recentDocuments.slice(0, 5).map((doc, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={doc.sourceFilename || 'Unknown source'}
                          secondary={
                            <Box>
                              <Chip
                                label={doc.sourceType}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ mr: 1 }}
                              />
                              {doc.similarity && (
                                <Typography variant="caption" color="text.secondary">
                                  Relevance: {Math.round(doc.similarity * 100)}%
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}

              {/* Raw Summary Preview */}
              {snaPreviewData.summary && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Formatted Summary (what gets injected)
                  </Typography>
                  <Paper sx={{
                    p: 2,
                    bgcolor: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    maxHeight: 300,
                    overflow: 'auto'
                  }}>
                    {snaPreviewData.summary}
                  </Paper>
                </Box>
              )}

              {(!snaPreviewData.stats || snaPreviewData.stats.totalDocuments === 0) && (
                <Alert severity="warning">
                  No SNA data has been indexed for this entity yet. The variable will return minimal content until documents are processed through the SNA pipeline.
                </Alert>
              )}
            </Box>
          ) : (
            <Typography color="text.secondary">
              No preview data available. Select an entity and try again.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnaPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert onClose={() => setError(null)} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PromptManagement;
